import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import {useFormik} from 'formik';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import API from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {AUTO_REFRESH_SELECT_ITEMS} from '../../constants/common';
import {useAppDispatch, useAppSelector} from '../../hooks/redux';
import {
  ConfigurationInput,
  ConfigurationResponse,
} from '../../interfaces/Configuration';
import reduxActions from '../../redux/actions';
import {COLORED_ICONS} from '../../utils/icons';
import {CloseSnackbarAction} from '../common/CloseSnackbarButton';
import {ColorSelect} from '../selectors/ColorSelect';
import {SoundSelect} from '../selectors/SoundSelect';

interface ConfigurationAmsValues {
  [key: string]: string | number | null;
}

const FIELDS_BY_SECTIONS = {
  autoRefreshSettings: [
    'ams.ams_grid_refresh_period',
    'ams.ams_emodule_grid_refresh_period',
  ],

  sensorAlarmAlertPairs: [
    // Alarm & Alert levels for every AMS sensor type, ordered to match the
    // SSP-217 mockup row-by-row. Each adjacent [alert, alarm] pair renders
    // as Alert | Alarm side-by-side in the 4-column grid, so each row shows
    // two sensor types. Mapping numeric IDs -> gas types is sourced from
    // backend migration 1715860276570-ams-updates.ts and the EmoduleTypeId
    // enum in services/Entity/Ams/Emodule/Type.ts.

    // Row 1: Air Flow (130) | Ammonia (8)
    'ams.ams_130_value_alert_level',
    'ams.ams_130_value_alarm_level',
    'ams.ams_8_value_alert_level',
    'ams.ams_8_value_alarm_level',
    // Row 2: Analog (0.4 - 2 V) (61) | Analog (4 - 20 mA) (60)
    'ams.ams_61_value_alert_level',
    'ams.ams_61_value_alarm_level',
    'ams.ams_60_value_alert_level',
    'ams.ams_60_value_alarm_level',
    // Row 3: Chlorine (1) | Carbon Dioxide / CO2 (43)
    'ams.ams_1_value_alert_level',
    'ams.ams_1_value_alarm_level',
    'ams.ams_43_value_alert_level',
    'ams.ams_43_value_alarm_level',
    // Row 4: Carbon Dioxide (IR) / IR-CO2 (45) | Carbon Monoxide / CO (0)
    'ams.ams_45_value_alert_level',
    'ams.ams_45_value_alarm_level',
    'ams.ams_0_value_alert_level',
    'ams.ams_0_value_alarm_level',
    // Row 5: Humidity (102) | Hydrogen (2) -- Hydrogen wasn't in the mockup
    // but exists in the DB; slotted alphabetically before Hydrogen Sulfide.
    'ams.ams_102_value_alert_level',
    'ams.ams_102_value_alarm_level',
    'ams.ams_2_value_alert_level',
    'ams.ams_2_value_alarm_level',
    // Row 6: Hydrogen Sulfide (3) | Methane (IR-CH4 #40) (40)
    'ams.ams_3_value_alert_level',
    'ams.ams_3_value_alarm_level',
    'ams.ams_40_value_alert_level',
    'ams.ams_40_value_alarm_level',
    // Row 7: Methane (IR-CH4 #44) (44) | Methane (IR-CH4 #40) Blast Mode
    // The mockup's "Methane (IR 44) Blast Mode" cell has no DB-backed field
    // (ams_44 has no blast variant), so it's omitted.
    'ams.ams_44_value_alert_level',
    'ams.ams_44_value_alarm_level',
    'ams.ams_40_value_alert_level_blast_mode',
    'ams.ams_40_value_alarm_level_blast_mode',
    // Row 8: Methane (PEL-CH4) (20) | Methane (PEL-CH4) Blast Mode
    'ams.ams_20_value_alert_level',
    'ams.ams_20_value_alarm_level',
    'ams.ams_20_value_alert_level_blast_mode',
    'ams.ams_20_value_alarm_level_blast_mode',
    // Row 9: Nitric Oxide (4) | Nitrogen Dioxide (5)
    'ams.ams_4_value_alert_level',
    'ams.ams_4_value_alarm_level',
    'ams.ams_5_value_alert_level',
    'ams.ams_5_value_alarm_level',
    // Row 10: Oxygen Lower (6) | Oxygen Upper (6)
    // Lower pair is intentionally rendered as Alarm | Alert (swapped).
    'ams.ams_6_lower_alarm_level',
    'ams.ams_6_lower_alert_level',
    'ams.ams_6_upper_alert_level',
    'ams.ams_6_upper_alarm_level',
    // Row 11: Pressure (103) | Sulfur Dioxide / SO2 (7)
    'ams.ams_103_value_alert_level',
    'ams.ams_103_value_alarm_level',
    'ams.ams_7_value_alert_level',
    'ams.ams_7_value_alarm_level',
    // Row 12: SW Monitored (81) | SW Namur (80)
    'ams.ams_81_value_alert_level',
    'ams.ams_81_value_alarm_level',
    'ams.ams_80_value_alert_level',
    'ams.ams_80_value_alarm_level',
    // Row 13: Temperature (62) | Temperature (#100) (100)
    'ams.ams_62_value_alert_level',
    'ams.ams_62_value_alarm_level',
    'ams.ams_100_value_alert_level',
    'ams.ams_100_value_alarm_level',
    // Row 14: Temperature (#101) (101) | -- the mockup's "Temperature (#102)"
    // doesn't exist as a field (ams_102 is Humidity, already in row 5).
    'ams.ams_101_value_alert_level',
    'ams.ams_101_value_alarm_level',
  ],

  sensorPreAlert: [
    'ams.ams_44_value_pre_alert_level',
    'ams.ams_20_value_pre_alert_level',
    'ams.ams_40_value_pre_alert_level',
  ],

  sensorLowBattery: ['ams.ams_node_low_battery'],

  sensorNoEnvMessages: ['ams.ams_no_env_messages'],

  sensorFewEnvMessages: [
    'ams.ams_few_env_messages_time',
    'ams.ams_few_env_messages_number_of_messages',
  ],

  sensorManyEnvMessages: ['ams.ams_many_env_messages'],

  sensorCalibrationDate: ['ams.ams_calibration_date_range'],

  sensorAlertAlarmDelay: ['ams.ams_alarm_delay'],

  blastModeStatus: ['ams.allow_blast_mode'],
  blastModePeriod: ['ams.blast_mode_period'],
  blastModeAlarmDelay: ['ams.ams_alarm_delay_blast_mode'],

  eventsBroadcast: ['ams-events.broadcast_alarm', 'ams-events.broadcast_alert'],

  calibrationMode: ['ams.emodule_calibration_timeout'],

  soundGlobal: ['ams.sound_ams_new_event'],

  sound: [
    'ams.sound_ams_new_detected',
    'ams.sound_ams_alarm',
    'ams.sound_ams_alert',
    'ams.sound_ams_pre_alert',
    'ams.sound_ams_ava_test',
    'ams.sound_ams_calibration_date_expired',
    'ams.sound_ams_emodule_unregistered',
    'ams.sound_ams_env_missing_sn',
    'ams.sound_ams_few_env_messages',
    'ams.sound_ams_input_state_change',
    'ams.sound_ams_low_battery',
    'ams.sound_ams_many_env_messages',
    'ams.sound_ams_sd_no_envs_message',
  ],

  color: [
    'ams-color.color_ams_alarm',
    'ams-color.color_ams_alert',
    'ams-color.color_ams_pre_alert',
    'ams-color.color_ams_ava_test',
    'ams-color.color_ams_calibration_date_expired',
    'ams-color.color_ams_emodule_unregistered',
    'ams-color.color_ams_env_missing_sn',
    'ams-color.color_ams_few_env_messages',
    //'ams-color.color_ams_input_state_change',
    'ams-color.color_ams_low_battery',
    'ams-color.color_ams_many_env_messages',
    'ams-color.color_ams_new_detected',
    'ams-color.color_ams_sd_no_envs_message',
  ],
};

const FIELDS = _.flatMap(FIELDS_BY_SECTIONS);

const getFormikValues = (
  item?: ConfigurationResponse
): ConfigurationAmsValues => ({
  ..._.chain(FIELDS)
    .map((name) => ({name, value: null}))
    .keyBy('name')
    .mapValues('value')
    .value(),

  ..._.chain(item)
    .filter((i) => FIELDS.includes(`${i.section}.${i.name}`))
    .keyBy((i) => `${i.section}.${i.name}`)
    .mapValues('value')
    .value(),
});

const getFormikValuesClear = (
  item?: ConfigurationResponse
): ConfigurationAmsValues => ({
  ..._.chain(item)
    .filter((i) => FIELDS.includes(`${i.section}.${i.name}`))
    .keyBy((i) => `${i.section}.${i.name}`)
    .mapValues('default')
    .value(),
});

export interface AdminAmsConfigProps {
  disabled?: boolean;
  onChangeSubmittedInProgress?: (value: boolean) => any;
  onChangeResetInProgress?: (value: boolean) => any;
}

export interface AdminAmsConfigRef {
  fetch?: Function;
  submit?: Function;
  reset?: Function;
  clear?: Function;
}

export const AdminAmsConfig = forwardRef<
  AdminAmsConfigRef,
  React.PropsWithChildren<AdminAmsConfigProps>
>(({disabled, onChangeSubmittedInProgress, onChangeResetInProgress}, ref) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const assetHumanGroups = useAppSelector(({assets}) => assets.asset_human_groups);

  /*******/
  /* ref */
  /*******/
  useImperativeHandle(ref, () => ({
    fetch: () => fetchData(),
    submit: () => formik.handleSubmit(),
    reset: () => handleResetData(),
    clear: () => formik.setValues(getFormikValuesClear(fetchedData)),
  }));

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] = useState<ConfigurationResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>();
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const fetchData = async () => {
    setFetchedInProgress(true);

    try {
      const params = {section: JSON.stringify(['ams', 'ams-color', 'ams-events'])};
      const resp = await API.get<ConfigurationResponse>(
        `${apiBaseUrl}/configuration`,
        {params}
      );
      setFetchedData(resp.data);
    } catch (error: any) {
      setFetchedErrors(error.data);
    }

    setFetchedInProgress(false);
  };

  useEffect(() => {
    fetchData();

    return () => {
      setFetchedData(undefined);
      setFetchedErrors(undefined);
    };
  }, []);

  /**********/
  /* submit */
  /**********/
  // const [submittedData, setSubmittedData] = useState<ConfigurationResponse>();
  // const [submittedErrors, setSubmittedErrors] = useState<string[]>();
  const [submittedInProgress, setSubmittedInProgress] = useState(false);
  const reduxDispatch = useAppDispatch();

  const submitData = async (data: ConfigurationInput) => {
    setSubmittedInProgress(true);

    try {
      const resp = await API.post<ConfigurationResponse>(
        `${apiBaseUrl}/configuration`,
        data
      );
      setFetchedData(resp.data);
      reduxDispatch(reduxActions.app.fetchMyConfigurations);
      enqueueSnackbar('Configuration successfully updated', {
        variant: 'success',
        action: CloseSnackbarAction,
      });
    } catch (error: any) {
      const message = error?.response?.data?.message ?? 'There is an error';
      enqueueSnackbar(message, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setSubmittedInProgress(false);
  };

  /**********/
  /* reset */
  /**********/
  const [resetInProgress, setResetInProgress] = useState(false);

  const resetData = async (data: ConfigurationInput) => {
    setResetInProgress(true);
    console.log(data);
    try {
      const resp = await API.patch<ConfigurationResponse>(
        `${apiBaseUrl}/configuration`,
        data
      );
      setFetchedData(resp.data);
      reduxDispatch(reduxActions.app.fetchMyConfigurations);
      enqueueSnackbar('Configuration successfully reset', {
        variant: 'success',
        action: CloseSnackbarAction,
      });
    } catch (error: any) {
      const message = error?.response?.data?.message ?? 'There is an error';
      enqueueSnackbar(message, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setResetInProgress(false);
  };

  const handleResetData = async () => {
    const input: ConfigurationInput = {
      section: ['ams', 'ams-color', 'ams-events'],
      configuration: _.map(formik.values, (value, key) => ({
        section: key.split('.')[0],
        name: key.split('.')[1],
        value,
      })),
    };
    await resetData(input);
  };

  /*********/
  /* input */
  /*********/
  const inputValidationSchema = yup.object();

  const formik = useFormik({
    initialValues: getFormikValues(fetchedData),
    validationSchema: inputValidationSchema,
    onSubmit: async (values) => {
      const input: ConfigurationInput = {
        company_level: true,
        section: ['ams', 'ams-color', 'ams-events'],
        configuration: _.map(values, (value, key) => ({
          section: key.split('.')[0],
          name: key.split('.')[1],
          value,
        })),
      };
      await submitData(input);
    },
  });

  useEffect(() => {
    const newInput = getFormikValues(fetchedData);
    if (!_.isEqual(newInput, formik.values)) {
      formik.setValues(newInput);
    }
  }, [fetchedData]);

  /************/
  /* keysData */
  /************/
  const keysData = useMemo(
    () => ({
      ..._.chain(fetchedData)
        .keyBy((i) => `${i.section}.${i.name}`)
        .mapValues()
        .value(),
    }),
    [fetchedData]
  );

  const broadcastOptions = useMemo(() => {
    return [
      {value: '0', name: 'Disabled'},
      {value: 'broadcast', name: 'Broadcast'},
      ...assetHumanGroups.map((group) => ({
        value: group.id.toString(),
        name: group.name,
      })),
    ];
  }, [assetHumanGroups]);

  // other
  useEffect(() => {
    onChangeSubmittedInProgress?.(submittedInProgress);
  }, [submittedInProgress]);

  useEffect(() => {
    onChangeResetInProgress?.(resetInProgress);
  }, [resetInProgress]);

  return (
    <Box position="relative">
      <Backdrop open={fetchedInProgress}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {fetchedErrors?.map((error, index) => (
        <Alert key={index} severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      ))}

      <Paper sx={{p: 3, mb: 3}}>
        <Box fontSize={20} lineHeight={1.6} mb={3}>
          AMS Auto Refresh Settings
        </Box>

        <Grid container spacing={4}>
          {FIELDS_BY_SECTIONS.autoRefreshSettings.map((name) => (
            <Grid key={name} item xs={12} lg={6}>
              <TextField
                fullWidth
                label={t(keysData[name]?.label)}
                value={formik.values[name] ?? 'None'}
                name={name}
                type="number"
                size="small"
                select
                disabled={disabled}
                onChange={(event) => {
                  const value =
                    event.target.value === 'None' ? null : event.target.value;
                  formik.setValues({...formik.values, [name]: value});
                }}
              >
                {AUTO_REFRESH_SELECT_ITEMS.map((i) => (
                  <MenuItem key={i ?? ''} value={i ?? ''}>
                    {i ?? 'None'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{p: 3, mb: 3}}>
        <Box fontSize={20} lineHeight={1.6} mb={3}>
          Blast Mode
        </Box>

        <Grid container spacing={4}>
            {FIELDS_BY_SECTIONS.blastModeStatus.map((name) => (
              <Grid key={name} item xs={12} lg={3}>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  select
                  disabled={disabled}
                  onChange={(event) =>
                    formik.setValues({
                      ...formik.values,
                      [name]: event.target.value,
                    })
                  }
                >
                  {[
                    {value: 0, name: 'Disabled'},
                    {value: 1, name: 'Enabled'},
                  ].map((i) => (
                    <MenuItem key={i.value ?? ''} value={i.value ?? ''}>
                      {i.name ?? 'None'}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ))}
            {FIELDS_BY_SECTIONS.blastModePeriod.map((name) => (
              <Grid key={name} item xs={12} lg={3}>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  disabled={disabled}
                  onChange={(event) =>
                    formik.setValues({
                      ...formik.values,
                      [name]: event.target.value,
                    })
                  }
                />
              </Grid>
            ))}
            {FIELDS_BY_SECTIONS.blastModeAlarmDelay.map((name) => (
              <Grid key={name} item xs={12} lg={3}>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  disabled={disabled}
                  inputProps={{min: 0, max: 600}}
                  onChange={(event) => {
                    const value = event.target.value;
                    const numValue = Number(value);
                    if (value === '' || (numValue >= 0 && numValue <= 600)) {
                      formik.setValues({
                        ...formik.values,
                        [name]: value,
                      });
                    }
                  }}
                />
              </Grid>
            ))}
        </Grid>
      </Paper>

      <Paper sx={{p: 3, mb: 3}}>
        <Box fontSize={20} lineHeight={1.6} mb={3}>
          Events Broadcast
        </Box>

        <Grid container spacing={4}>
          {FIELDS_BY_SECTIONS.eventsBroadcast.map((name) => (
            <Grid key={name} item xs={12} lg={6}>
              <TextField
                fullWidth
                label={t(keysData[name]?.label)}
                value={formik.values[name]?.toString() ?? '0'}
                name={name}
                size="small"
                select
                disabled={disabled}
                onChange={(event) => {
                  const value = event.target.value;
                  const finalValue = value === '0' ? 0
                                    : value === 'broadcast' ? 'broadcast'
                                    : parseInt(value, 10);
                  formik.setValues({
                    ...formik.values,
                    [name]: finalValue,
                  });
                }}
              >
                {broadcastOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{p: 3, mb: 3}}>
        <Box fontSize={20} lineHeight={1.6} mb={3}>
          AMS Sensor Alarm &amp; Alert Levels
        </Box>

        <Grid container spacing={4}>
          {FIELDS_BY_SECTIONS.sensorAlarmAlertPairs.map((name) => (
            <Grid key={name} item xs={12} lg={3}>
              <TextField
                fullWidth
                label={t(keysData[name]?.label)}
                value={formik.values[name] ?? ''}
                name={name}
                type="number"
                size="small"
                disabled={disabled}
                onChange={(event) =>
                  formik.setValues({
                    ...formik.values,
                    [name]: event.target.value,
                  })
                }
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{p: 3, mb: 3}}>
        <Box fontSize={20} lineHeight={1.6} mb={3}>
          AMS Sensor Pre-Alert
        </Box>

        <Grid container spacing={4}>
          {FIELDS_BY_SECTIONS.sensorPreAlert.map((name) => (
            <Grid key={name} item xs={12} lg={3}>
              <TextField
                fullWidth
                label={t(keysData[name]?.label)}
                value={formik.values[name] ?? ''}
                name={name}
                type="number"
                size="small"
                disabled={disabled}
                onChange={(event) =>
                  formik.setValues({
                    ...formik.values,
                    [name]: event.target.value,
                  })
                }
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{p: 3, mb: 3}}>
        <Box fontSize={20} lineHeight={1.6} mb={2}>
          AMS Sensor
        </Box>

        <Grid container spacing={4} mb={3}>
          <Grid item xs={12} lg={4}>
            <Box fontSize={14} lineHeight={1.6} mb={2}>
              Sensor Low Battery
            </Box>

            {FIELDS_BY_SECTIONS.sensorLowBattery.map((name) => (
              <Box key={name}>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  disabled={disabled}
                  onChange={(event) =>
                    formik.setValues({
                      ...formik.values,
                      [name]: event.target.value,
                    })
                  }
                />
              </Box>
            ))}
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box fontSize={14} lineHeight={1.6} mb={2}>
              Sensor Calibration Date
            </Box>

            {FIELDS_BY_SECTIONS.sensorCalibrationDate.map((name) => (
              <Box key={name}>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  disabled={disabled}
                  onChange={(event) =>
                    formik.setValues({
                      ...formik.values,
                      [name]: event.target.value,
                    })
                  }
                />
              </Box>
            ))}
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box fontSize={14} lineHeight={1.6} mb={2}>
              Sensor Alert/Alarm Delay
            </Box>

            {FIELDS_BY_SECTIONS.sensorAlertAlarmDelay.map((name) => (
              <Box key={name}>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  disabled={disabled}
                  inputProps={{min: 0, max: 600}}
                  onChange={(event) => {
                    const value = event.target.value;
                    const numValue = Number(value);
                    if (value === '' || (numValue >= 0 && numValue <= 600)) {
                      formik.setValues({
                        ...formik.values,
                        [name]: value,
                      });
                    }
                  }}
                />
              </Box>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12} lg={4} mb={3}>
          <Box fontSize={14} lineHeight={1.6} mb={2}>
            Calibration Mode
          </Box>

          {FIELDS_BY_SECTIONS.calibrationMode.map((name) => (
            <Box key={name}>
              <TextField
                fullWidth
                label={t(keysData[name]?.label)}
                value={formik.values[name] ?? ''}
                name={name}
                type="number"
                size="small"
                disabled={disabled}
                onChange={(event) =>
                  formik.setValues({
                    ...formik.values,
                    [name]: event.target.value,
                  })
                }
              />
            </Box>
          ))}
        </Grid>

        <Box fontSize={14} lineHeight={1.6} mb={2}>
          Few ENV Messages
        </Box>

        <Grid container spacing={4} mb={3}>
          {FIELDS_BY_SECTIONS.sensorFewEnvMessages.map((name) => (
            <Grid key={name} item xs={12} lg={6}>
              <Box>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  disabled={disabled}
                  onChange={(event) =>
                    formik.setValues({
                      ...formik.values,
                      [name]: event.target.value,
                    })
                  }
                />
              </Box>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={4} mb={3}>
          <Grid item xs={12} lg={6}>
            <Box fontSize={14} lineHeight={1.6} mb={2}>
              No ENV Messages
            </Box>

            {FIELDS_BY_SECTIONS.sensorNoEnvMessages.map((name) => (
              <Box key={name}>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  disabled={disabled}
                  onChange={(event) =>
                    formik.setValues({
                      ...formik.values,
                      [name]: event.target.value,
                    })
                  }
                />
              </Box>
            ))}
          </Grid>

          <Grid item xs={12} lg={6}>
            <Box fontSize={14} lineHeight={1.6} mb={2}>
              Many ENV Messages
            </Box>

            {FIELDS_BY_SECTIONS.sensorManyEnvMessages.map((name) => (
              <Box key={name}>
                <TextField
                  fullWidth
                  label={t(keysData[name]?.label)}
                  value={formik.values[name] ?? ''}
                  name={name}
                  type="number"
                  size="small"
                  disabled={disabled}
                  onChange={(event) =>
                    formik.setValues({
                      ...formik.values,
                      [name]: event.target.value,
                    })
                  }
                />
              </Box>
            ))}
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={4} mb={3}>
        <Grid item xs={12} lg={6} xl={8}>
          <Paper sx={{p: 3, mb: 3}}>
            <Box fontSize={20} lineHeight={1.6} mb={2}>
              AMS Sound
            </Box>

            <Box fontSize={14} lineHeight={1.6} mb={2}>
              Global
            </Box>

            <Grid container spacing={4} mb={6}>
              <Grid item xs={6}>
                {FIELDS_BY_SECTIONS.soundGlobal.slice(0, 1).map((name) => (
                  <SoundSelect
                    key={name}
                    fullWidth
                    label={t(keysData[name]?.label)}
                    value={formik.values[name]}
                    name={name}
                    size="small"
                    disabled={disabled}
                    onChange={(event) =>
                      formik.setValues({
                        ...formik.values,
                        [name]: event.target.value,
                      })
                    }
                  />
                ))}
              </Grid>
            </Grid>

            <Grid container spacing={4} mb={3}>
              {FIELDS_BY_SECTIONS.sound.map((name) => (
                <Grid key={name} item xs={6}>
                  <SoundSelect
                    fullWidth
                    label={t(keysData[name]?.label)}
                    value={formik.values[name] ?? ''}
                    name={name}
                    size="small"
                    disabled={disabled}
                    onChange={(event) =>
                      formik.setValues({
                        ...formik.values,
                        [name]: event.target.value,
                      })
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6} xl={4}>
          <Paper sx={{p: 3, mb: 3}}>
            <Box fontSize={20} lineHeight={1.6} mb={2}>
              AMS Color
            </Box>

            <Grid container spacing={4} mb={3}>
              {FIELDS_BY_SECTIONS.color.map((name) => (
                <Grid key={name} item xs={6}>
                  <ColorSelect
                    fullWidth
                    size="small"
                    label={t(keysData[name]?.label)}
                    value={formik.values[name] ?? ''}
                    name={name}
                    disabled={disabled}
                    icons={COLORED_ICONS.unknown}
                    onChange={(event) =>
                      formik.setValues({
                        ...formik.values,
                        [name]: event.target.value,
                      })
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
});
